/**
 * KanbanCard Component
 *
 * Represents a single card in the kanban board
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { BoardTask } from '@/types/models/BoardTask';
import { Card } from '@/components/ui/card';

import { useThemeColors, withOpacity, opacity } from '@/styles';
import { layout, typography, spacing } from '@/styles/design-tokens';
import { ShareButton } from '@/components/core/ShareButton';
import { DeepLinkEntity } from '@/utils/deep-linking';
import { BoardTaskPriority } from '@/types/enums/BoardTaskPriority';
import { BoardTaskReflection } from '@/types/enums/BoardTaskReflection';
import { usePlayfulHandlers } from '@/styles/playful';
import { formatDate } from '@/utils/date';

// A count above this always renders as a single "🍅×N" badge, never as one
// icon per unit. Bad/unbounded data (e.g. a corrupted actual_pomodoros value)
// must never turn into tens of thousands of DOM nodes and freeze the tab.
const MAX_EXPANDED_POMODOROS = 20;

function renderPomodoroIcons(
  count: number,
  green = false,
  compact = false
): React.ReactNode {
  const big = Math.floor(count / 10);
  const small = count % 10;
  const greenFilter = green ? 'hue-rotate(120deg) saturate(1.5)' : undefined;

  if (compact || count > MAX_EXPANDED_POMODOROS) {
    return (
      <>
        <span style={{ fontSize: typography.sizes.sm, filter: greenFilter }}>
          🍅
        </span>
        <span style={{ fontSize: typography.sizes.xs, marginLeft: 1 }}>
          ×{count}
        </span>
      </>
    );
  }

  return (
    <>
      {Array.from({ length: big }).map((_, i) => (
        <span
          key={`big-${i}`}
          style={{
            fontSize: typography.sizes.sm,
            lineHeight: 'normal',
            filter: greenFilter,
          }}
        >
          🍅
        </span>
      ))}
      {Array.from({ length: small }).map((_, i) => (
        <span
          key={`small-${i}`}
          style={{
            fontSize: typography.sizes.xs,
            lineHeight: 'normal',
            opacity: 0.9,
            filter: greenFilter,
          }}
        >
          🍅
        </span>
      ))}
    </>
  );
}

interface KanbanCardProps {
  card: BoardTask;
  isNew?: boolean;
  animationDelay?: number;
  onDragStart?: (e: React.DragEvent, card: BoardTask) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onClick?: (card: BoardTask) => void;
  /** Whether this card is frozen (past week, non-admin) */
  isFrozen?: boolean;
  /** Whether the current user lacks assignee/mentor/admin scoping to move or edit this card */
  isEditForbidden?: boolean;
  /** When set, replaces pomodoro counts with a creator name badge */
  creatorName?: string;
}

export default function KanbanCard({
  card,
  isNew = false,
  animationDelay = 0,
  onDragStart,
  onDragEnd,
  onClick,
  isFrozen = false,
  isEditForbidden = false,
  creatorName,
}: KanbanCardProps) {
  const colors = useThemeColors();
  const isLocked = isFrozen || isEditForbidden;
  const playful = usePlayfulHandlers({ disabled: isLocked });
  const [isDragging, setIsDragging] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  // 0 = both expanded, 1 = estimated compact, 2 = both compact
  const [compactLevel, setCompactLevel] = useState(0);

  // Reset to 0 when the row is resized so we re-evaluate the minimum needed level
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const observer = new ResizeObserver(() => setCompactLevel(0));
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    if (row.scrollWidth > row.clientWidth) {
      setCompactLevel(prev => (prev < 2 ? prev + 1 : prev));
    }
  });

  // Scroll the first new card into view so the animation is visible
  useEffect(() => {
    if (!isNew || animationDelay !== 0) return;
    const timer = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
    return () => clearTimeout(timer);
  }, [isNew, animationDelay]);

  const handleDragStart = (e: React.DragEvent) => {
    if (isLocked) {
      e.preventDefault();
      return;
    }
    setIsDragging(true);
    onDragStart?.(e, card);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setIsDragging(false);
    onDragEnd?.(e);
  };

  const handleClick = () => {
    // Only trigger click if not currently dragging
    if (!isDragging) {
      onClick?.(card);
    }
  };

  const getReflectionDot = (reflection: string) => {
    const key =
      reflection === BoardTaskReflection.UNDERSTOOD
        ? 'understood'
        : reflection === BoardTaskReflection.STILL_THINKING
          ? 'stillThinking'
          : 'needHelp';
    const { text } = colors.reflection[key];
    const label =
      reflection === BoardTaskReflection.UNDERSTOOD
        ? 'Understood'
        : reflection === BoardTaskReflection.STILL_THINKING
          ? 'Still thinking'
          : 'Need help';
    return (
      <span
        title={label}
        style={{
          position: 'absolute',
          top: spacing.xs,
          left: spacing.xs,
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: text,
          display: 'inline-block',
          zIndex: 9 /* one below ShareButton's z-10 at top-right */,
        }}
      />
    );
  };

  const getPriorityStyle = (priority: string) => {
    switch (priority) {
      case BoardTaskPriority.HIGH:
        return {
          backgroundColor: withOpacity(colors.error, opacity.medium),
          color: colors.error,
        };
      case BoardTaskPriority.MEDIUM:
        return {
          backgroundColor: withOpacity(colors.primary, opacity.medium),
          color: colors.primary,
        };
      case BoardTaskPriority.LOW:
        return {
          backgroundColor: withOpacity(colors.success, opacity.medium),
          color: colors.success,
        };
      default:
        return {
          backgroundColor: withOpacity(colors.primary, opacity.medium),
          color: colors.primary,
        };
    }
  };

  return (
    <Card
      ref={cardRef}
      data-card-id={card.id}
      className={`group relative p-4 ${isLocked ? 'cursor-default' : 'cursor-pointer'} hover:shadow-soft-lg transition-all duration-300 ease-out flex flex-col rounded-xl ${
        isDragging ? 'opacity-50 scale-95' : ''
      } ${isNew ? 'animate-card-enter' : ''}`}
      style={{
        backgroundColor: colors.background,
        height: layout.kanban.cardHeight,
        animationDelay: isNew ? `${animationDelay}ms` : undefined,
        opacity: isLocked ? 0.7 : undefined,
        ...playful.style,
      }}
      title={
        isEditForbidden
          ? "You don't have permission to move this card"
          : undefined
      }
      draggable={!isLocked}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onMouseEnter={playful.onMouseEnter}
      onMouseLeave={playful.onMouseLeave}
    >
      {/* Hover-reveal copy link button */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
        <ShareButton
          entity={DeepLinkEntity.CARD}
          id={card.id!}
          ariaLabel="Copy card link"
        />
      </div>

      {card.reflection ? getReflectionDot(card.reflection) : null}

      <span
        className="font-semibold mb-2 line-clamp-2 overflow-hidden"
        style={{ color: colors.text.primary }}
      >
        {card.title}
      </span>
      {card.description && (
        <span
          className="text-sm mb-2 flex-1 line-clamp-1 overflow-hidden"
          style={{ color: colors.text.secondary }}
        >
          {card.description}
        </span>
      )}
      {(() => {
        // Deadline pill: red when overdue (and card not yet completed), neutral otherwise.
        const deadlineDate = card.deadline ? new Date(card.deadline) : null;
        const isValidDeadline =
          !!deadlineDate && !isNaN(deadlineDate.getTime());
        const isOverdue =
          isValidDeadline && !card.completed_at && deadlineDate! < new Date();
        const deadlinePill = isValidDeadline ? (
          <span
            className="px-2 py-1.5 rounded-md shrink-0"
            style={{
              backgroundColor: withOpacity(
                isOverdue ? colors.error : colors.text.secondary,
                opacity.medium
              ),
              color: isOverdue ? colors.error : colors.text.secondary,
            }}
            title={isOverdue ? 'Overdue' : 'Deadline'}
          >
            ⏰ {formatDate(card.deadline)}
          </span>
        ) : null;

        if (creatorName) {
          return (
            <div className="flex gap-2 text-xs mt-auto overflow-hidden items-center">
              {card.priority ? (
                <span
                  className="px-3 py-1.5 rounded-md shrink-0"
                  style={getPriorityStyle(card.priority)}
                >
                  {card.priority}
                </span>
              ) : null}
              {deadlinePill}
              <span
                className="px-3 py-1.5 rounded-md shrink-0 truncate"
                style={{
                  backgroundColor: withOpacity(
                    colors.text.secondary,
                    opacity.subtle
                  ),
                  color: colors.text.secondary,
                }}
              >
                {creatorName}
              </span>
            </div>
          );
        }

        const hasBottomRow =
          card.priority ||
          card.estimated_pomodoros ||
          card.actual_pomodoros ||
          isValidDeadline ||
          card.reflection;
        if (!hasBottomRow) return null;

        return (
          <div
            ref={rowRef}
            className="flex gap-2 text-xs mt-auto overflow-hidden items-center"
          >
            {card.priority ? (
              <span
                className="px-3 py-1.5 rounded-md shrink-0"
                style={getPriorityStyle(card.priority)}
              >
                {card.priority}
              </span>
            ) : null}
            {deadlinePill}
            {card.estimated_pomodoros ? (
              <span
                className="px-2 py-1.5 rounded-md flex items-center gap-px shrink-0"
                style={{
                  backgroundColor: withOpacity(colors.blue, opacity.medium),
                  color: colors.blue,
                }}
              >
                {renderPomodoroIcons(
                  card.estimated_pomodoros,
                  false,
                  compactLevel >= 1
                )}
              </span>
            ) : null}
            {card.actual_pomodoros ? (
              <span
                className="px-2 py-1.5 rounded-md flex items-center gap-px shrink-0"
                style={{
                  backgroundColor: withOpacity(colors.success, opacity.medium),
                  color: colors.success,
                }}
              >
                {renderPomodoroIcons(
                  card.actual_pomodoros,
                  true,
                  compactLevel >= 2
                )}
              </span>
            ) : null}
          </div>
        );
      })()}
    </Card>
  );
}
